import React, { useEffect, useState, useMemo } from 'react';
import { IconTools, IconBook2 } from '@tabler/icons-react';
import { FaCheckCircle, FaClipboardList } from 'react-icons/fa';
import { motion } from 'framer-motion';

const RingChart = ({ value, color }) => {
  const percentage = Math.min(Math.max(value, 0), 100);
  const strokeDasharray = 314;
  const strokeDashoffset = strokeDasharray * (1 - percentage / 100);

  return (
    <motion.svg
      width="120"
      height="120"
      viewBox="0 0 120 120"
      className="drop-shadow-xl"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <circle cx="60" cy="60" r="50" stroke="#e5e7eb" strokeWidth="10" fill="none" />
      <motion.circle
        cx="60"
        cy="60"
        r="50"
        stroke={color}
        strokeWidth="10"
        fill="none"
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDasharray}
        strokeLinecap="round"
        animate={{ strokeDashoffset }}
        transition={{ duration: 1, ease: 'easeInOut' }}
      />
      <motion.text
        x="60"
        y="66"
        textAnchor="middle"
        fontSize="22"
        fontWeight="bold"
        fill={color}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6 }}
      >
        {percentage}%
      </motion.text>
    </motion.svg>
  );
};

const getColorByPercentage = (percentage) => {
  if (percentage < 40) return '#EE1D23'; // red
  if (percentage <= 70) return '#facc15'; // yellow
  return '#22c55e'; // green
};

const ToolAndPKTSection = ({
  effectivenessPercentage = 0,
  pktPercentage = 0,
  pktLabel = 'PKT Score',
  auditTarget = 0,
  auditCompleted = 0,
  loading = false,
}) => {
  const [displayedCompleted, setDisplayedCompleted] = useState(0);
  const [displayedPercentage, setDisplayedPercentage] = useState(0);

  useEffect(() => {
    let startCompleted = 0;
    let startPercentage = 0;
    const endCompleted = auditCompleted || 0;
    const endPercentage = auditTarget ? Math.round((auditCompleted / auditTarget) * 100) : 0;

    const duration = 1500;
    const incrementCompleted = Math.ceil(endCompleted / 50);
    const incrementPercentage = Math.ceil(endPercentage / 50);

    const timer = setInterval(() => {
      startCompleted += incrementCompleted;
      startPercentage += incrementPercentage;

      if (startCompleted >= endCompleted) {
        clearInterval(timer);
        setDisplayedCompleted(endCompleted);
        setDisplayedPercentage(endPercentage);
      } else {
        setDisplayedCompleted(startCompleted);
        setDisplayedPercentage(startPercentage);
      }
    }, duration / 50);

    return () => clearInterval(timer);
  }, [auditCompleted, auditTarget]);

  const progressColor = useMemo(() => {
    return getColorByPercentage(displayedPercentage);
  }, [displayedPercentage]);

  const textColor = useMemo(() => {
    if (displayedPercentage < 40) return "text-red-600";
    if (displayedPercentage <= 70) return "text-yellow-600";
    return "text-green-600";
  }, [displayedPercentage]);

  const data = [
    {
      icon: <IconTools size={36} className="text-blue-900" />,
      title: "Tool Effectiveness",
      percentage: parseFloat(effectivenessPercentage),
      color: getColorByPercentage(parseFloat(effectivenessPercentage)),
      desc: "Efficiency in tool usage across audits",
    },
    {
      icon: <IconBook2 size={36} className="text-red-600" />,
      title: pktLabel,
      percentage: parseFloat(pktPercentage),
      color: getColorByPercentage(parseFloat(pktPercentage)),
      desc: "Knowledge & protocol test scoring",
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 px-4 mb-12">
      {data.map((item, i) => (
        <motion.div
          key={i}
          className="relative bg-white/40 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-xl overflow-hidden p-6 flex flex-col items-center transition-transform duration-300 hover:scale-[1.03]"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.3, duration: 0.8 }}
        >
          <div className="absolute top-0 left-0 w-full h-[8px] bg-gradient-to-r from-[#EE1D23] via-pink-400 to-[#1A2B5C] rounded-t-3xl z-10" />
          <div className="relative z-20 mb-4 p-4 bg-white/60 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center">
            {item.icon}
          </div>
          <h3 className="text-xl font-bold text-[#1A2B5C] mb-2 z-20">{item.title}</h3>
          <div className="mt-2 mb-3 z-20">
            <RingChart value={item.percentage} color={item.color} />
          </div>
          <p className="text-gray-700 text-sm text-center mt-1 z-20">{item.desc}</p>
          <p className="text-xs text-center mt-1 z-20">
            <span className="text-red-600 font-semibold">🔴 Red</span>: 10–40%,{' '}
            <span className="text-yellow-600 font-semibold">🟡 Yellow</span>: 41–70%,{' '}
            <span className="text-green-600 font-semibold">🟢 Green</span>: 71–100%
          </p>
        </motion.div>
      ))}

      {/* Audit Count Card */}
      <div className="relative bg-white/40 backdrop-blur-xl border border-gray-200 rounded-3xl shadow-xl overflow-hidden p-6 flex flex-col items-center">
        <div className="absolute top-0 left-0 w-full h-[8px] bg-gradient-to-r from-[#EE1D23] via-pink-400 to-[#1A2B5C] rounded-t-3xl z-10" />
        <div className="relative z-20 mb-4 p-4 bg-white/60 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center">
          <FaClipboardList className="text-gray-700" size={36} />
        </div>
        <h3 className="text-xl font-bold text-[#1A2B5C] mb-2 z-20">Audit Count</h3>

        <div className="grid grid-cols-2 gap-6 mt-2 mb-3 items-center">
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-600">Completed</p>
            <p className={`mt-1 text-3xl font-bold ${textColor}`}>{displayedCompleted}</p>
          </div>
          <div className="relative flex items-center justify-center bg-gray-200 rounded-xl p-4 shadow-md">
            <FaCheckCircle className="text-green-500 text-2xl mr-2" />
            <div>
              <p className="text-sm font-semibold text-gray-700">Target</p>
              <p className="text-xl font-bold text-gray-900">{auditTarget}</p>
            </div>
          </div>
        </div>

        <div className="relative w-full h-5 bg-gray-200 rounded-lg overflow-hidden mt-4 shadow-inner">
          <div
            className={`absolute top-0 left-0 h-full rounded-lg`}
            style={{ backgroundColor: progressColor, width: `${displayedPercentage}%` }}
          />
        </div>

        <p className={`text-center text-lg font-bold ${textColor} mt-3`}>
          {displayedPercentage}% Achieved
        </p>
      </div>
    </div>
  );
};

export default ToolAndPKTSection;
